import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import { ArrowLeft, Play, Pause, Loader2, Volume2, Film, Sparkles } from 'lucide-react';

const subjectIcons = { Math: '📐', Science: '🧬', English: '📖', 'Social Science': '🌍', Hindi: '🔤', EVS: '🌿' };

export default function VideoLibrary({ onNavigate }) {
  const { api, user } = useAuth();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [playingId, setPlayingId] = useState(null);
  const [generatingFor, setGeneratingFor] = useState(null);
  const [activeAudio, setActiveAudio] = useState(null);
  const audioRef = useRef(null);

  const className = user?.class_name || 'Class 8';
  const subjects = ['Math', 'Science', 'English', 'Social Science', 'Hindi', 'EVS'];

  const fetchVideos = useCallback(async () => {
    try {
      const res = await api.get(`/video-library?class_name=${className}`);
      setVideos(res.data);
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [api, className]);

  useEffect(() => { fetchVideos(); }, [fetchVideos]);

  // Stop audio on unmount (navigation away)
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  // Function to stop audio - exposed for external use
  const stopAudio = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setPlayingId(null);
    setActiveAudio(null);
  }, []);

  const generateVideo = async (subject, chapterNum) => {
    setGeneratingFor(`${subject}-${chapterNum}`);
    try {
      const res = await api.post('/ai/generate-audio', {
        subject,
        chapter_number: chapterNum,
        class_name: className,
      });
      fetchVideos();
      if (res.data.audio_base64) {
        playAudio(res.data);
      }
    } catch (err) { console.error(err); }
    setGeneratingFor(null);
  };

  const playAudio = async (video) => {
    if (playingId === video.id) {
      stopAudio();
      return;
    }

    try {
      const res = await api.get(`/audio-videos/${video.id}`);
      const fullVideo = res.data;

      if (fullVideo.audio_base64) {
        if (audioRef.current) { audioRef.current.pause(); }
        const audio = new Audio(`data:audio/mp3;base64,${fullVideo.audio_base64}`);
        audio.onended = () => { setPlayingId(null); audioRef.current = null; };
        audioRef.current = audio;
        audio.play();
        setPlayingId(video.id);
        setActiveAudio(fullVideo);
        api.post(`/audio-videos/${video.id}/play`).catch(() => {});
      }
    } catch (err) { console.error(err); }
  };

  // Custom navigation that stops audio first
  const handleNavigate = (page, params) => {
    stopAudio();
    onNavigate(page, params);
  };

  return (
    <div className="pb-nav px-4 pt-6 max-w-lg mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button data-testid="back-from-library" onClick={() => handleNavigate('dashboard')} className="text-slate-400 hover:text-white">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-xl font-bold text-white font-['Outfit']" data-testid="library-title">Video Library</h1>
          <p className="text-xs text-slate-500">{className} - 3-min explainers with audio</p>
        </div>
      </div>

      {/* Now Playing */}
      {playingId && activeAudio && (
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
          className="bg-violet-600/10 border border-violet-500/30 rounded-2xl p-4 mb-5" data-testid="now-playing">
          <div className="flex items-center gap-3">
            <button onClick={() => playAudio(activeAudio)} className="w-10 h-10 rounded-full bg-violet-600 flex items-center justify-center">
              <Pause className="w-5 h-5 text-white" />
            </button>
            <div className="flex-1">
              <p className="text-sm font-medium text-white">{activeAudio.chapter_name}</p>
              <p className="text-xs text-violet-300">{activeAudio.subject} - Now Playing</p>
            </div>
            <Volume2 className="w-5 h-5 text-violet-400 animate-pulse" />
          </div>
        </motion.div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3 mb-5">
        <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-3 text-center">
          <p className="text-xl font-bold text-violet-400 font-['Outfit']">{videos.length}</p>
          <p className="text-[10px] text-slate-500">Generated</p>
        </div>
        <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-3 text-center">
          <p className="text-xl font-bold text-cyan-400 font-['Outfit']">
            {videos.reduce((sum, v) => sum + (v.plays || 0), 0)}
          </p>
          <p className="text-[10px] text-slate-500">Total Plays</p>
        </div>
        <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-3 text-center">
          <p className="text-xl font-bold text-amber-400 font-['Outfit']">{30 - videos.length}</p>
          <p className="text-[10px] text-slate-500">Remaining</p>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-6 h-6 text-violet-500 animate-spin" />
        </div>
      ) : (
        <>
          {/* Generated Videos */}
          {videos.length > 0 && (
            <div className="mb-5">
              <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-widest mb-3">Generated Videos</h2>
              <div className="space-y-2">
                {videos.map(v => (
                  <motion.div
                    key={v.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    data-testid={`video-${v.subject}-${v.chapter_number}`}
                    className={`bg-slate-900/50 border rounded-xl p-4 flex items-center gap-3 transition-all ${
                      playingId === v.id ? 'border-violet-500/40 bg-violet-500/5' : 'border-slate-800 hover:border-slate-700'
                    }`}
                  >
                    <button
                      data-testid={`play-${v.id}`}
                      onClick={() => playAudio(v)}
                      className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${
                        playingId === v.id ? 'bg-violet-600 animate-pulse' : 'bg-slate-800 hover:bg-violet-600/50'
                      }`}
                    >
                      {playingId === v.id ? <Pause className="w-4 h-4 text-white" /> : <Play className="w-4 h-4 text-white ml-0.5" />}
                    </button>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-white truncate">
                        {subjectIcons[v.subject]} Ch{v.chapter_number}: {v.chapter_name}
                      </p>
                      <p className="text-xs text-slate-500">{v.duration_estimate} w/ audio | {v.plays} plays</p>
                    </div>
                    {v.has_audio && <Volume2 className="w-4 h-4 text-emerald-400 shrink-0" />}
                  </motion.div>
                ))}
              </div>
            </div>
          )}

          {/* Generate Missing */}
          <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-widest mb-3">Generate Missing Videos</h2>
          <div className="space-y-2">
            {subjects.map(s => {
              const generated = videos.filter(v => v.subject === s);
              const missing = [1, 2, 3, 4, 5].filter(n => !generated.some(v => v.chapter_number === n));
              if (missing.length === 0) return null;
              return (
                <div key={s} className="bg-slate-900/50 border border-slate-800 rounded-xl p-4">
                  <p className="text-sm font-medium text-white mb-2">{subjectIcons[s]} {s} ({5 - missing.length}/5 done)</p>
                  <div className="flex flex-wrap gap-2">
                    {missing.map(n => (
                      <button
                        key={n}
                        data-testid={`gen-${s}-${n}`}
                        onClick={() => generateVideo(s, n)}
                        disabled={generatingFor === `${s}-${n}`}
                        className="bg-violet-600/10 border border-violet-500/20 text-violet-300 rounded-lg px-3 py-1.5 text-xs hover:bg-violet-600/20 transition-all flex items-center gap-1 disabled:opacity-50"
                      >
                        {generatingFor === `${s}-${n}` ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <Film className="w-3 h-3" />
                        )}
                        Ch{n}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
